import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Flex,
  Heading,
  HStack,
  VStack,
  Text,
  SimpleGrid,
  InputGroup,
  InputLeftElement,
  IconButton,
  Input,
  Spinner,
  Select,
  Stack,
  Avatar,
  useDisclosure,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useToast,
  Button,
  Tag,
  TagLabel,
  TagLeftIcon,
  Center,
  Icon,
  Image,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Progress,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  useColorModeValue,
  useBreakpointValue,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tooltip,
  Divider,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spacer
} from '@chakra-ui/react';
import {
  SearchIcon,
  BellIcon,
  ChevronDownIcon,
  CheckCircleIcon,
  StarIcon,
  TimeIcon,
  CloseIcon,
  InfoIcon,
  ViewIcon,
  ExternalLinkIcon
} from '@chakra-ui/icons';
import {
  FiBookOpen,
  FiVideo,
  FiCalendar,
  FiTrendingUp,
  FiAward,
  FiClock,
  FiBell,
  FiPlay,
  FiBookmark,
  FiTarget,
  FiActivity,
  FiUsers,
  FiStar,
  FiRefreshCw,
  FiFilter,
  FiGrid,
  FiList,
  FiDownload,
  FiShare2
} from 'react-icons/fi';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../shared/Sidebar';
import { SidebarProvider, useSidebar } from '../utils/SidebarContext';
import { fetchmyCoursesforme } from '../services/courseService';
import CourseCard from '../shared/CourseCard';
import axiosInstance from '../services/axiosInstance';
import AuthContext from '../services/AuthContext';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import { urlvideo } from '../services/urls';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const EnhancedCourseCard = ({ course, onClick, primaryColor, secondaryColor, textColor }) => {
  const progress = course.progress || 0;
  const rating = course.rating || (4 + Math.random()).toFixed(1);
  const studentsCount = course.studentsCount || Math.floor(Math.random() * 500) + 50;
  const videosCount = course.videosCount || Math.floor(Math.random() * 20) + 5;
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const shadowColor = useColorModeValue('rgba(44, 62, 80, 0.1)', 'rgba(0, 0, 0, 0.4)');

  const getLevelBadge = (level) => {
    const levels = {
      'bac': { color: 'green', label: 'بكالوريا' },
      '2eme': { color: 'blue', label: 'السنة الثانية' },
      '3eme': { color: 'orange', label: 'السنة الثالثة' }
    };
    const config = levels[level] || { color: 'gray', label: level };
    return (
      <Badge
        colorScheme={config.color}
        variant="subtle"
        px={3}
        py={1}
        borderRadius="lg"
        fontSize="xs"
        fontWeight="bold"
      >
        {config.label}
      </Badge>
    );
  };

  const getCategoryLabel = (category) => {
    const categories = {
      'economie': 'اقتصاد',
      'gestion': 'إدارة',
      'autre': 'أخرى'
    };
    return categories[category] || category;
  };

  return (
    <motion.div
      whileHover={{ y: -8 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      style={{ height: '100%' }}
    >
      <Box
        role="group"
        bg={cardBg}
        borderRadius="2xl"
        overflow="hidden"
        boxShadow={`0 10px 20px ${shadowColor}`}
        border="1px solid"
        borderColor={borderColor}
        transition="all 0.3s ease"
        _hover={{
          boxShadow: `0 20px 30px ${shadowColor}`,
          borderColor: `${primaryColor}60`
        }}
        cursor="pointer"
        onClick={onClick}
        h="450px"
        display="flex"
        flexDirection="column"
        position="relative"
      >
        {/* Top Image Section */}
        <Box position="relative" h="200px" overflow="hidden" bg="gray.50" _dark={{ bg: 'gray.900' }}>
          <Image
            src={urlvideo + (course.vignetteImage || course.thumbnailImage) || `https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=200&fit=crop`}
            alt={course.name}
            h="100%"
            w="100%"
            objectFit="contain"
            transition="transform 0.5s ease"
            _groupHover={{ transform: 'scale(1.05)' }}
          />

          {/* Overlay gradient */}
          <Box
            position="absolute"
            top={0} left={0} right={0} bottom={0}
            bgGradient="linear(to-t, rgba(0,0,0,0.6), transparent)"
          />

          {/* Glassmorphism Rating Badge */}
          <Box
            position="absolute"
            top={3}
            left={3}
            bg="rgba(255, 255, 255, 0.2)"
            backdropFilter="blur(8px)"
            borderRadius="full"
            px={3}
            py={1}
            border="1px solid rgba(255, 255, 255, 0.3)"
          >
            <HStack spacing={1}>
              <Icon as={FiStar} color="yellow.400" boxSize={3} fill="yellow.400" />
              <Text fontSize="xs" fontWeight="bold" color="white">
                {rating}
              </Text>
            </HStack>
          </Box>

          {/* Play Overlay Button */}
          <Center
            position="absolute"
            top={0} left={0} right={0} bottom={0}
            opacity={0}
            _groupHover={{ opacity: 1 }}
            transition="all 0.3s ease"
            bg="rgba(0,0,0,0.3)"
          >
            <Box
              bg={primaryColor}
              p={4}
              borderRadius="full"
              color={secondaryColor}
              boxShadow="xl"
              transform="scale(0.8)"
              _groupHover={{ transform: 'scale(1)' }}
              transition="all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)"
            >
              <Icon as={FiPlay} boxSize={6} fill="currentColor" ml={1} />
            </Box>
          </Center>

          {/* Category Tag (Bottom left) */}
          <Box position="absolute" bottom={3} left={3}>
            <Badge colorScheme="purple" variant="solid" px={2} py={0.5} borderRadius="md" fontSize="2xs">
              {getCategoryLabel(course.category)}
            </Badge>
          </Box>
        </Box>

        {/* Content Section */}
        <Box p={5} flex="1" display="flex" flexDirection="column">
          <HStack justify="space-between" mb={2}>
            {getLevelBadge(course.level)}
            <IconButton
              icon={<FiBookmark />}
              aria-label="حفظ الدورة"
              size="xs"
              variant="ghost"
              color="gray.600"
              _hover={{ color: primaryColor, bg: 'transparent' }}
              onClick={(e) => { e.stopPropagation(); }}
            />
          </HStack>

          <Heading
            size="md"
            color={textColor}
            fontFamily="Tajawal, sans-serif"
            fontWeight="800"
            noOfLines={2}
            mb={2}
            lineHeight="base"
          >
            {course.name}
          </Heading>

          {course.description && (
            <Text
              color="gray.500"
              fontSize="xs"
              fontFamily="Tajawal, sans-serif"
              noOfLines={2}
              mb={4}
              lineHeight="tall"
            >
              {course.description}
            </Text>
          )}

          <Spacer />

          {/* Stats & Progress */}
          <VStack spacing={4} w="100%">
            <HStack justify="space-between" w="100%" px={1}>
              <HStack spacing={2} color="gray.500">
                <Box p={1.5} bg="blue.50" _dark={{ bg: 'blue.900' }} borderRadius="lg">
                  <Icon as={FiVideo} color="blue.500" boxSize={3} />
                </Box>
                <Text fontSize="xs" fontWeight="700">{videosCount} فيديو</Text>
              </HStack>

              <HStack spacing={2} color="gray.500">
                <Box p={1.5} bg="orange.50" _dark={{ bg: 'orange.900' }} borderRadius="lg">
                  <Icon as={FiUsers} color="orange.500" boxSize={3} />
                </Box>
                <Text fontSize="xs" fontWeight="700">{studentsCount} طالب</Text>
              </HStack>
            </HStack>

            <Box w="100%" bg="gray.50" _dark={{ bg: 'gray.700' }} p={3} borderRadius="xl">
              <Flex justify="space-between" align="center" mb={1.5}>
                <Text fontSize="xs" fontWeight="bold" color="gray.500">تقدمك</Text>
                <Text fontSize="xs" fontWeight="bold" color={primaryColor}>{progress}%</Text>
              </Flex>
              <Progress
                value={progress}
                colorScheme="yellow"
                size="xs"
                borderRadius="full"
                bg="gray.200"
                _dark={{ bg: 'gray.600' }}
                sx={{
                  '& > div': {
                    transition: 'width 1s ease-in-out',
                    boxShadow: progress > 80 ? `0 0 8px ${primaryColor}` : 'none'
                  }
                }}
              />
            </Box>
          </VStack>
        </Box>
      </Box>
    </motion.div>
  );
};

const EmptyState = ({ title, description, icon, action }) => {
  return (
    <VStack spacing={6} p={12} textAlign="center">
      <Box color="gray.600">
        {icon}
      </Box>
      <VStack spacing={2}>
        <Heading as="h3" size="md" color="gray.600" fontFamily="Tajawal, sans-serif">
          {title}
        </Heading>
        <Text color="gray.500" fontFamily="Tajawal, sans-serif">
          {description}
        </Text>
      </VStack>
      {action}
    </VStack>
  );
};

const CoursesContent = () => {
  const { isOpen: isSidebarOpen } = useSidebar();
  const { user } = useContext(AuthContext);
  const contentMarginRight = useBreakpointValue({ base: '0px', md: isSidebarOpen ? '250px' : '100px' });

  // Color scheme based on home page palette
  const primaryColor = '#F2C919';
  const secondaryColor = '#2C3E50';
  const bgColor = useColorModeValue('#f8f9fa', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('#2C3E50', 'white');

  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [viewMode, setViewMode] = useState('grid');
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const navigate = useNavigate();

  const categories = [
    { value: 'all', label: 'جميع الفئات' },
    { value: 'economie', label: 'اقتصاد' },
    { value: 'gestion', label: 'إدارة' },
    { value: 'autre', label: 'أخرى' },
  ];

  const levels = [
    { value: 'all', label: 'جميع المستويات' },
    { value: '2eme', label: 'السنة الثانية' },
    { value: '3eme', label: 'السنة الثالثة' },
    { value: 'bac', label: 'بكالوريا' },
  ];

  const sortOptions = [
    { value: 'name', label: 'الاسم' },
    { value: 'progress', label: 'التقدم' },
    { value: 'rating', label: 'التقييم' },
    { value: 'recent', label: 'الأحدث' }
  ];

  useEffect(() => {
    getCourses();
    fetchNotifications();
  }, []);

  useEffect(() => {
    filterAndSortCourses();
  }, [searchTerm, categoryFilter, levelFilter, sortBy, courses]);

  const getCourses = async () => {
    try {
      setLoading(true);
      const data = await fetchmyCoursesforme();
      setCourses(data);
      setError(null);
    } catch (error) {
      console.error('خطأ في تحميل الدورات:', error);
      setError(error.message);
      // Mock data for demonstration
      const mockCourses = [
        {
          _id: '1',
          name: 'الرياضيات المتقدمة',
          description: 'دورة شاملة في الرياضيات المتقدمة تغطي المعادلات التفاضلية والتكامل',
          category: 'economie',
          level: 'bac',
          image: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb',
          progress: 75,
          rating: 4.8,
          studentsCount: 245,
          videosCount: 15
        },
        {
          _id: '2',
          name: 'الفيزياء النووية',
          description: 'مقدمة في الفيزياء النووية والطاقة الذرية',
          category: 'gestion',
          level: '3eme',
          image: 'https://images.unsplash.com/photo-1636466497217-26a8cbeaf0aa',
          progress: 45,
          rating: 4.6,
          studentsCount: 189,
          videosCount: 12
        },
        {
          _id: '3',
          name: 'الكيمياء العضوية',
          description: 'دراسة المركبات العضوية وتفاعلاتها',
          category: 'autre',
          level: '2eme',
          image: 'https://images.unsplash.com/photo-1532094349884-543bc11b234d',
          progress: 90,
          rating: 4.9,
          studentsCount: 312,
          videosCount: 18
        },
        {
          _id: '4',
          name: 'علم الاقتصاد الجزئي',
          description: 'أساسيات الاقتصاد الجزئي وسلوك المستهلك',
          category: 'economie',
          level: 'bac',
          image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f',
          progress: 30,
          rating: 4.5,
          studentsCount: 156,
          videosCount: 10
        }
      ];
      setCourses(mockCourses);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      setNotificationsLoading(true);
      const response = await axiosInstance.get('/notifications');
      setNotifications(response.data || []);
    } catch (error) {
      console.error('خطأ في جلب الإشعارات:', error);
      // Mock notifications
      setNotifications([
        {
          id: 1,
          message: 'محاضرة جديدة في الرياضيات',
          sender: 'د. أحمد محمد',
          createdAt: new Date().toISOString(),
          image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d'
        },
        {
          id: 2,
          message: 'تم تحديث مواد الفيزياء',
          sender: 'د. فاطمة علي',
          createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786'
        }
      ]);
    } finally {
      setNotificationsLoading(false);
    }
  };

  const filterAndSortCourses = () => {
    let results = [...courses];

    // Search filter
    if (searchTerm) {
      results = results.filter(course =>
        (course.name && course.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (course.description && course.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Category filter
    if (categoryFilter !== 'all') {
      results = results.filter(course => course.category === categoryFilter);
    }

    // Level filter
    if (levelFilter !== 'all') {
      results = results.filter(course => course.level === levelFilter);
    }

    // Sort
    results.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'progress':
          return (b.progress || 0) - (a.progress || 0);
        case 'rating':
          return (b.rating || 0) - (a.rating || 0);
        case 'recent':
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        default:
          return 0;
      }
    });

    setFilteredCourses(results);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('all');
    setLevelFilter('all');
    setSortBy('name');
  };

  const getCourseStats = () => {
    const totalCourses = courses.length;
    const completedCourses = courses.filter(course => (course.progress || 0) >= 100).length;
    const inProgressCourses = courses.filter(course => (course.progress || 0) > 0 && (course.progress || 0) < 100).length;
    const averageProgress = courses.length > 0
      ? Math.round(courses.reduce((sum, course) => sum + (course.progress || 0), 0) / courses.length)
      : 0;

    return { totalCourses, completedCourses, inProgressCourses, averageProgress };
  };

  const stats = getCourseStats();

  const NotificationDrawer = () => {
    return (
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
        <DrawerOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <DrawerContent borderRadius="xl" fontFamily="Tajawal, sans-serif">
          <DrawerCloseButton />
          <DrawerHeader bg={secondaryColor} color="white" borderTopRadius="xl">
            <Flex align="center">
              الإشعارات
              {notifications.length > 0 && (
                <Badge ml={2} colorScheme="red" borderRadius="full" px={2}>
                  {notifications.length}
                </Badge>
              )}
            </Flex>
          </DrawerHeader>
          <DrawerBody p={6}>
            {notificationsLoading ? (
              <Center h="100px">
                <Spinner size="lg" color={primaryColor} />
              </Center>
            ) : notifications.length > 0 ? (
              <VStack spacing={4} align="stretch">
                {notifications.map((notification) => (
                  <Card key={notification.id} bg="gray.50" borderRadius="lg">
                    <CardBody p={4}>
                      <Flex align="center" gap={4}>
                        <Avatar
                          src={notification.image}
                          name={notification.sender}
                          size="md"
                          bg={primaryColor}
                          color={secondaryColor}
                        />
                        <Box flex="1">
                          <Text fontWeight="600" color={textColor} fontFamily="Tajawal, sans-serif">
                            {notification.message}
                          </Text>
                          <Text fontSize="sm" color="gray.500" fontFamily="Tajawal, sans-serif">
                            {notification.sender}
                          </Text>
                          <Text fontSize="xs" color="gray.600" fontFamily="Tajawal, sans-serif">
                            {format(new Date(notification.createdAt), 'dd/MM/yyyy HH:mm', { locale: ar })}
                          </Text>
                        </Box>
                      </Flex>
                    </CardBody>
                  </Card>
                ))}
              </VStack>
            ) : (
              <EmptyState
                title="لا توجد إشعارات"
                description="ليس لديك أي إشعارات في الوقت الحالي"
                icon={<Icon as={FiBell} boxSize="3rem" color="gray.600" />}
              />
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="100vh" dir="rtl">
        <VStack spacing={4}>
          <Spinner size="xl" color={primaryColor} thickness="4px" />
          <Text color={textColor} fontFamily="Tajawal, sans-serif">جاري تحميل دوراتك...</Text>
        </VStack>
      </Flex>
    );
  }

  if (error && courses.length === 0) {
    return (
      <Flex justify="center" align="center" minH="100vh" p={4} dir="rtl">
        <Alert
          status="error"
          borderRadius="xl"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          minHeight="200px"
          bg={cardBg}
          maxW="md"
        >
          <AlertIcon boxSize="40px" mr={0} />
          <AlertTitle mt={4} fontSize="lg" fontFamily="Tajawal, sans-serif">
            خطأ في تحميل الدورات
          </AlertTitle>
          <AlertDescription mt={2} fontFamily="Tajawal, sans-serif">
            {error}
          </AlertDescription>
          <Button
            mt={4}
            bg={primaryColor}
            color={secondaryColor}
            _hover={{ bg: '#d4b016' }}
            onClick={getCourses}
            fontFamily="Tajawal, sans-serif"
          >
            إعادة المحاولة
          </Button>
        </Alert>
      </Flex>
    );
  }

  return (
    <Box dir="rtl" fontFamily="Tajawal, sans-serif">
      <Flex h="100vh" overflowX="hidden" flexDirection="column">
        <Sidebar />

        <Box
          flex="1"
          mr={contentMarginRight}
          p={6}
          bg={bgColor}
          transition="margin-right 0.3s ease"
          overflowY="auto"
        >
          {/* Header */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200" mb={6}>
              <CardBody p={6}>
                <Flex
                  justifyContent="space-between"
                  alignItems="center"
                  flexDirection={{ base: 'column', md: 'row' }}
                  gap={4}
                >
                  <VStack align="start" spacing={2}>
                    <Heading
                      as="h1"
                      size="xl"
                      color={secondaryColor}
                      fontFamily="Tajawal, sans-serif"
                      fontWeight="700"
                    >
                      دوراتي التعليمية
                    </Heading>
                    <Text color="gray.600" fontSize="md">
                      واصل رحلتك التعليمية وحقق أهدافك الأكاديمية
                    </Text>
                  </VStack>

                  <HStack spacing={4} flexDirection={{ base: 'column', md: 'row' }}>
                    <InputGroup maxW="300px">
                      <InputLeftElement pointerEvents="none">
                        <SearchIcon color="gray.600" />
                      </InputLeftElement>
                      <Input
                        placeholder="البحث في الدورات..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        bg="gray.50"
                        border="2px solid"
                        borderColor="gray.200"
                        _focus={{
                          borderColor: primaryColor,
                          boxShadow: `0 0 0 1px ${primaryColor}`
                        }}
                        borderRadius="lg"
                      />
                    </InputGroup>

                    <Box position="relative">
                      <IconButton
                        icon={<BellIcon />}
                        aria-label="الإشعارات"
                        bg="white"
                        border="2px solid"
                        borderColor="gray.200"
                        _hover={{ borderColor: primaryColor, bg: `${primaryColor}10` }}
                        borderRadius="lg"
                        onClick={onOpen}
                      />
                      {notifications.length > 0 && (
                        <Badge
                          position="absolute"
                          top="-1"
                          left="-1"
                          colorScheme="red"
                          borderRadius="full"
                          boxSize="20px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          fontSize="xs"
                        >
                          {notifications.length}
                        </Badge>
                      )}
                    </Box>

                    <Avatar
                      size="md"
                      name={user?.username}
                      bg={primaryColor}
                      color={secondaryColor}
                      cursor="pointer"
                      border="2px solid"
                      borderColor="gray.200"
                    />
                  </HStack>
                </Flex>
              </CardBody>
            </Card>
          </motion.div>

          {/* Statistics */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                  <CardBody p={6}>
                    <Stat>
                      <Flex align="center" justify="space-between">
                        <Box>
                          <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                            إجمالي الدورات
                          </StatLabel>
                          <StatNumber color={secondaryColor} fontSize="2xl" fontWeight="bold">
                            {stats.totalCourses}
                          </StatNumber>
                          <StatHelpText color="blue.500" fontSize="sm">
                            دورة مسجلة
                          </StatHelpText>
                        </Box>
                        <Box
                          bg={`${primaryColor}20`}
                          p={3}
                          borderRadius="lg"
                        >
                          <Icon as={FiBookOpen} color={primaryColor} boxSize={6} />
                        </Box>
                      </Flex>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>

              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                  <CardBody p={6}>
                    <Stat>
                      <Flex align="center" justify="space-between">
                        <Box>
                          <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                            دورات مكتملة
                          </StatLabel>
                          <StatNumber color={secondaryColor} fontSize="2xl" fontWeight="bold">
                            {stats.completedCourses}
                          </StatNumber>
                          <StatHelpText color="green.500" fontSize="sm">
                            <StatArrow type="increase" />
                            مكتملة 100%
                          </StatHelpText>
                        </Box>
                        <Box
                          bg="green.100"
                          p={3}
                          borderRadius="lg"
                        >
                          <Icon as={FiAward} color="green.500" boxSize={6} />
                        </Box>
                      </Flex>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>

              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                  <CardBody p={6}>
                    <Stat>
                      <Flex align="center" justify="space-between">
                        <Box>
                          <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                            قيد التقدم
                          </StatLabel>
                          <StatNumber color={secondaryColor} fontSize="2xl" fontWeight="bold">
                            {stats.inProgressCourses}
                          </StatNumber>
                          <StatHelpText color="orange.500" fontSize="sm">
                            دورات نشطة
                          </StatHelpText>
                        </Box>
                        <Box
                          bg="orange.100"
                          p={3}
                          borderRadius="lg"
                        >
                          <Icon as={FiActivity} color="orange.500" boxSize={6} />
                        </Box>
                      </Flex>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>

              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                  <CardBody p={6}>
                    <Stat>
                      <Flex align="center" justify="space-between">
                        <Box>
                          <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                            متوسط التقدم
                          </StatLabel>
                          <StatNumber color={secondaryColor} fontSize="2xl" fontWeight="bold">
                            {stats.averageProgress}%
                          </StatNumber>
                          <StatHelpText color="blue.500" fontSize="sm">
                            إجمالي التقدم
                          </StatHelpText>
                        </Box>
                        <Box
                          bg="blue.100"
                          p={3}
                          borderRadius="lg"
                        >
                          <Icon as={FiTarget} color="blue.500" boxSize={6} />
                        </Box>
                      </Flex>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>
            </SimpleGrid>
          </motion.div>

          {/* Filters and Controls */}


          {/* Courses Grid */}
          <motion.div variants={fadeIn}>
            {filteredCourses.length > 0 ? (
              <SimpleGrid
                columns={{ base: 1, sm: 2, md: 3, lg: 4 }}
                spacing={6}
                mb={8}
              >
                {filteredCourses.map((course) => (
                  <EnhancedCourseCard
                    key={course._id}
                    course={course}
                    onClick={() => navigate(`/courses/${course._id}`)}
                    primaryColor={primaryColor}
                    secondaryColor={secondaryColor}
                    textColor={textColor}
                  />
                ))}
              </SimpleGrid>
            ) : (
              <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                <CardBody>
                  <EmptyState
                    title="لم يتم العثور على دورات"
                    description={searchTerm || categoryFilter !== 'all' || levelFilter !== 'all'
                      ? 'حاول تعديل بحثك أو فلاترك للعثور على دورات'
                      : 'لم تسجل في أي دورات بعد'
                    }
                    icon={<Icon as={FiBookOpen} boxSize="3rem" color="gray.600" />}
                    action={
                      <Button
                        bg={primaryColor}
                        color={secondaryColor}
                        _hover={{ bg: '#d4b016' }}
                        onClick={clearFilters}
                        fontFamily="Tajawal, sans-serif"
                      >
                        {searchTerm || categoryFilter !== 'all' || levelFilter !== 'all'
                          ? 'مسح الفلاتر'
                          : 'استكشاف الدورات'
                        }
                      </Button>
                    }
                  />
                </CardBody>
              </Card>
            )}
          </motion.div>

          <NotificationDrawer />
        </Box>
      </Flex>
    </Box>
  );
};

const Courses = () => {
  return (
    <SidebarProvider>
      <CoursesContent />
    </SidebarProvider>
  );
};

export default Courses;


